// packages/web/src/components/Common/TimezoneSelector.tsx
import React, { useMemo, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Box,
  Typography,
  ListSubheader,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';

// Type declarations for Intl API
declare namespace Intl {
  function supportedValuesOf(key: 'timeZone'): string[];
  
  interface DateTimeFormatPartTypesRegistry {
    timeZoneName: any;
  }
  
  interface DateTimeFormatPart {
    type: string;
    value: string;
  }
  
  interface DateTimeFormat {
    formatToParts(date?: Date | number): DateTimeFormatPart[];
    resolvedOptions(): {
      timeZone?: string;
      [key: string]: any;
    };
  }
  
  interface DateTimeFormatConstructor {
    new(locales?: string | string[], options?: any): DateTimeFormat;
    (): DateTimeFormat;
  }
  
  const DateTimeFormat: DateTimeFormatConstructor;
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: 100,
  [theme.breakpoints.up('sm')]: {
    minWidth: 140,
  },
  '& .MuiInputBase-root': {
    height: 32,
    fontSize: '0.85rem',
    backgroundColor: 'white',
    borderRadius: '6px',
    fontWeight: 500,
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#84b817',
      },
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#84b817',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.85rem',
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#84b817',
    },
  },
  '& .MuiSelect-select': {
    padding: '6px 10px',
  },
}));

const SearchBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25),
  position: 'sticky',
  top: 0,
  backgroundColor: 'white',
  zIndex: 1,
  borderBottom: '2px solid #f0f8e8',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: '6px',
    fontSize: '0.85rem',
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#84b817',
      },
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#84b817',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputAdornment-root': {
    color: '#84b817',
  },
}));

const StyledListSubheader = styled(ListSubheader)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  backgroundColor: '#f0f8e8',
  color: '#1a1a1a',
  letterSpacing: '0.05em',
  padding: theme.spacing(0.75, 2),
  lineHeight: '1.8',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.85rem',
  fontWeight: 500,
  padding: theme.spacing(1, 3),
  color: '#1a1a1a',
  '&:hover': {
    backgroundColor: '#f0f8e8',
  },
  '&.Mui-selected': {
    backgroundColor: '#f9fdf0',
    borderLeft: '3px solid #c4d600',
    '&:hover': {
      backgroundColor: '#f0f8e8',
    },
  },
}));

// Popular timezones that appear first
const POPULAR_TIMEZONES = [
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Australia/Sydney',
  'America/Toronto',
  'Asia/Dubai',
  'Pacific/Auckland',
];

// Helper to get all available timezones
const getAllTimezones = (): string[] => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch (error) {
    console.warn('Intl.supportedValuesOf not available, using fallback list');
    // Fallback to a comprehensive list if the API is not available
    return [
      'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
      'America/Toronto', 'America/Vancouver', 'America/Mexico_City', 'America/Sao_Paulo',
      'America/Buenos_Aires', 'America/Santiago', 'America/Halifax', 'America/Anchorage',
      'America/Phoenix', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
      'Europe/Rome', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna', 'Europe/Warsaw',
      'Europe/Stockholm', 'Europe/Athens', 'Europe/Moscow', 'Europe/Istanbul', 'Europe/Zurich',
      'Europe/Dublin', 'Europe/Lisbon', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Hong_Kong',
      'Asia/Singapore', 'Asia/Seoul', 'Asia/Bangkok', 'Asia/Dubai', 'Asia/Mumbai',
      'Asia/Delhi', 'Asia/Kolkata', 'Asia/Jakarta', 'Asia/Manila', 'Asia/Taipei',
      'Asia/Kuala_Lumpur', 'Asia/Tel_Aviv', 'Asia/Jerusalem', 'Australia/Sydney',
      'Australia/Melbourne', 'Australia/Perth', 'Australia/Brisbane', 'Pacific/Auckland',
      'Pacific/Fiji', 'Pacific/Honolulu', 'Africa/Cairo', 'Africa/Lagos',
      'Africa/Johannesburg', 'Africa/Nairobi', 'Africa/Casablanca', 'Atlantic/Reykjavik',
      'Atlantic/Azores', 'UTC',
    ];
  }
};

// Get timezone info
interface TimezoneInfo {
  value: string;
  city: string;
  region: string;
  abbreviation: string;
  offset: string;
  displayLabel: string;
}

const getTimezoneInfo = (timezone: string): TimezoneInfo => {
  const parts = timezone.split('/');
  const region = parts[0] || 'Other';
  const city = parts[parts.length - 1]?.replace(/_/g, ' ') || timezone;
  
  // Get abbreviation and offset
  let abbreviation = '';
  let offset = '';
  
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    
    const parts = formatter.formatToParts(now);
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    abbreviation = tzPart?.value || '';
    
    // Calculate UTC offset
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes >= 0 ? '+' : '-';
    offset = `UTC${sign}${offsetHours}${offsetMins > 0 ? `:${offsetMins.toString().padStart(2, '0')}` : ''}`;
  } catch (error) {
    abbreviation = '';
    offset = 'UTC';
  }
  
  const displayLabel = `${city}, ${abbreviation || 'N/A'}, ${offset}`;
  
  return {
    value: timezone,
    city,
    region,
    abbreviation,
    offset,
    displayLabel,
  };
};

// Helper to get browser timezone
export const getBrowserTimezone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'UTC';
  } catch (error) {
    return 'UTC';
  }
};

// Find closest timezone from available list
export const findClosestTimezone = (browserTimezone: string): string => {
  if (!browserTimezone) return 'UTC';
  
  const allTimezones = getAllTimezones();
  
  // Check if browser timezone exists in our list
  if (allTimezones.includes(browserTimezone)) {
    return browserTimezone;
  }
  
  // Try to match by city name
  const browserCity = browserTimezone.split('/').pop()?.toLowerCase();
  if (browserCity) {
    const match = allTimezones.find(tz => 
      tz.toLowerCase().endsWith(`/${browserCity}`)
    );
    if (match) return match;
  }
  
  // Fall back to common patterns
  const lowerBrowserTz = browserTimezone.toLowerCase();
  
  if (lowerBrowserTz.includes('new_york') || lowerBrowserTz.includes('eastern')) {
    return 'America/New_York';
  }
  if (lowerBrowserTz.includes('los_angeles') || lowerBrowserTz.includes('pacific')) {
    return 'America/Los_Angeles';
  }
  if (lowerBrowserTz.includes('chicago') || lowerBrowserTz.includes('central')) {
    return 'America/Chicago';
  }
  if (lowerBrowserTz.includes('denver') || lowerBrowserTz.includes('mountain')) {
    return 'America/Denver';
  }
  
  return 'UTC';
};

// Format timezone for display
export const formatTimezoneForDisplay = (timezone: string): string => {
  if (!timezone) return 'UTC';
  
  const info = getTimezoneInfo(timezone);
  return info.displayLabel;
};

// Component props interface
interface TimezoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

// Main component
export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  value,
  onChange,
  size = 'small',
  fullWidth = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const safeValue = value || 'UTC';
  
  // Memoize timezone data
  const { groupedTimezones, popularTimezones } = useMemo(() => {
    const allTz = getAllTimezones();
    
    // Get info for all timezones
    const tzInfoList = allTz.map(getTimezoneInfo);
    
    // Separate popular timezones
    const popular = POPULAR_TIMEZONES
      .filter(tz => allTz.includes(tz))
      .map(getTimezoneInfo);
    
    // Group remaining timezones by region
    const grouped = tzInfoList.reduce((acc, tz) => {
      if (POPULAR_TIMEZONES.includes(tz.value)) return acc;
      
      if (!acc[tz.region]) {
        acc[tz.region] = [];
      }
      acc[tz.region].push(tz);
      return acc;
    }, {} as Record<string, TimezoneInfo[]>);
    
    // Sort within each group
    Object.keys(grouped).forEach(region => {
      grouped[region].sort((a, b) => a.city.localeCompare(b.city));
    });
    
    return { groupedTimezones: grouped, popularTimezones: popular };
  }, []);
  
  // Filter timezones based on search
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) {
      return groupedTimezones;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered: Record<string, TimezoneInfo[]> = {};
    
    Object.entries(groupedTimezones).forEach(([region, timezones]) => {
      const matches = timezones.filter(tz => 
        tz.city.toLowerCase().includes(query) ||
        tz.value.toLowerCase().includes(query)
      );
      
      if (matches.length > 0) {
        filtered[region] = matches;
      }
    });
    
    return filtered;
  }, [groupedTimezones, searchQuery]);
  
  const filteredPopular = useMemo(() => {
    if (!searchQuery.trim()) {
      return popularTimezones;
    }
    
    const query = searchQuery.toLowerCase();
    return popularTimezones.filter(tz => 
      tz.city.toLowerCase().includes(query) ||
      tz.value.toLowerCase().includes(query)
    );
  }, [popularTimezones, searchQuery]);
  
  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value);
  };
  
  return (
    <StyledFormControl size={size} fullWidth={fullWidth}>
      <InputLabel>Timezone</InputLabel>
      <Select
        value={safeValue}
        label="Timezone"
        onChange={handleChange}
        MenuProps={{
          PaperProps: {
            sx: { 
              maxHeight: 480,
              borderRadius: '8px',
              border: '1px solid #e5e5e5',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            }
          },
          autoFocus: false,
        }}
      >
        <SearchBox>
          <StyledTextField
            size="small"
            placeholder="Search city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </SearchBox>
        
        {filteredPopular.length > 0 && (
          <StyledListSubheader>
            POPULAR
          </StyledListSubheader>
        )}
        {filteredPopular.map(tz => (
          <StyledMenuItem key={tz.value} value={tz.value}>
            {tz.displayLabel}
          </StyledMenuItem>
        ))}
        
        {Object.entries(filteredGroups).map(([region, timezones]) => [
          <StyledListSubheader key={`group-${region}`}>
            {region.toUpperCase()}
          </StyledListSubheader>,
          ...timezones.map(tz => (
            <StyledMenuItem key={tz.value} value={tz.value}>
              {tz.displayLabel}
            </StyledMenuItem>
          ))
        ]).flat()}
      </Select>
    </StyledFormControl>
  );
};